/**
 * RecruiterVisibilityPage — `/recruiter-visibility` landing route.
 *
 * A focused, Apple-quiet funnel for the recruiter-visibility promise:
 *   Hero → How it works → Before / After → Compact pricing card →
 *   Everything-in-one-sitting → FAQ → Final CTA.
 *
 * Currency detection (INR / USD) is reused from the existing
 * ``fetchServerCurrency`` helper the homepage already ships, so
 * Indian IPs see ₹999 and everyone else sees $49 automatically.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  FileEdit,
  Sparkles,
  UserCheck,
  Star,
} from 'lucide-react';
import { AppShell } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../lib/auth-context';
import SeoHead from '../components/seo/SeoHead';
import { getOrAssignABVariant } from '../lib/referralTracker';
import { trackEvent } from '../lib/analytics';

import { BeforeAfterSection } from '../components/landing/BeforeAfterSection';
import { RecruiterTestimonials } from '../components/landing/RecruiterTestimonials';
import { RecruiterPricingCard } from '../components/landing/RecruiterPricingCard';
import { EverythingInOneSittingSection } from '../components/landing/EverythingInOneSittingSection';
import { FAQSection } from '../components/landing/FAQSection';
import { FinalCTA } from '../components/landing/FinalCTA';
import {
  fadeUp,
  kicker,
  displayHero,
  displaySection,
  sectionWrap,
  sectionInner,
} from '../components/landing/shared';

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
const TRUST_AVATARS = [
  { src: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'MS' },
  { src: 'https://images.unsplash.com/photo-1603985156220-90fc062e0521?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'DM' },
  { src: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'AR' },
  { src: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'PR' },
  { src: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'DL' },
];

const RecruiterHero = ({ onPrimaryCta, headlineVariant }) => {
  const isShort = headlineVariant === 'short';
  return (
  <section
    className="relative overflow-hidden"
    aria-label="Recruiter visibility hero"
    data-testid="recruiter-hero"
    data-ab-headline={headlineVariant}
    id="pricing"
  >
    {/* Soft, Apple-quiet gradient wash — light theme only. */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          'radial-gradient(900px circle at 82% 12%, hsl(212 92% 70% / 0.14), transparent 55%),' +
          'radial-gradient(700px circle at 8% 88%, hsl(28 92% 74% / 0.10), transparent 60%)',
      }}
    />
    <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24 lg:pt-28 pb-14 sm:pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Left column — copy + CTA + trust cluster */}
        <div className="lg:col-span-7">
          <motion.p
            {...fadeUp}
            className={`${kicker}`}
            data-testid="recruiter-hero-kicker"
          >
            Résumé × LinkedIn Makeover
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.05 }}
            className={`${displayHero} mt-5 text-[36px] sm:text-[52px] lg:text-[60px]`}
            data-testid="recruiter-hero-headline"
          >
            {isShort ? (
              <>
                <span className="text-primary">More interviews.</span>{' '}
                <span className="text-primary">Better opportunities.</span>
              </>
            ) : (
              <>
                Résumé and LinkedIn makeovers for{' '}
                <span className="text-primary">more interviews</span> and{' '}
                <span className="text-primary">better opportunities</span>.
              </>
            )}
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl"
            data-testid="recruiter-hero-subhead"
          >
            We combine AI and expert-curated insights to highlight your strengths,
            and help recruiters see your potential clearly.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <Button
              size="lg"
              onClick={onPrimaryCta}
              className="h-12 rounded-full px-7 text-[14.5px]"
              data-testid="recruiter-hero-primary-cta"
            >
              Start my makeover
              <ArrowRight size={16} className="ml-2" aria-hidden="true" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2.5" aria-hidden="true">
                {TRUST_AVATARS.map((a, i) => (
                  <Avatar key={i} className="h-8 w-8 ring-2 ring-background shadow-sm">
                    <AvatarImage
                      src={a.src}
                      alt=""
                      className="object-cover"
                      loading="lazy"
                    />
                    <AvatarFallback className="bg-secondary text-foreground text-[10px] font-medium">
                      {a.fb}
                    </AvatarFallback>
                  </Avatar>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      size={12}
                      className="text-primary fill-primary"
                      aria-hidden="true"
                    />
                  ))}
                  <span className="text-[12px] font-medium text-foreground/85 ml-1">
                    4.9 / 5
                  </span>
                </div>
                <p className="mt-0.5 text-[12px] text-muted-foreground">
                  Trusted by candidates hitting hiring managers this week.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right column — the pricing card, embedded in the hero so
            the buy decision is one glance from the headline. */}
        <div className="lg:col-span-5">
          <RecruiterPricingCard bare />
        </div>
      </div>
    </div>
  </section>
  );
};

// ---------------------------------------------------------------------------
// How it works — 3 quiet steps
// ---------------------------------------------------------------------------
const STEPS = [
  {
    n: '01',
    Icon: FileEdit,
    title: 'Share your profile',
    body: 'Upload your LinkedIn export or paste your URL. Takes under 90 seconds — we handle the rest.',
    testid: 'recruiter-step-1',
  },
  {
    n: '02',
    Icon: Sparkles,
    title: 'AI + expert audit',
    body: 'We benchmark you against real top performers in your space, surface the gaps recruiters notice, and rewrite in your voice.',
    testid: 'recruiter-step-2',
  },
  {
    n: '03',
    Icon: UserCheck,
    title: 'Ship the makeover',
    body: 'Download a recruiter-ready résumé and updated LinkedIn copy. Publish and start getting inbound within days.',
    testid: 'recruiter-step-3',
  },
];

const HowItWorks = () => (
  <section
    className={sectionWrap}
    id="how-it-works"
    aria-label="How it works"
  >
    <div className={sectionInner}>
      <motion.div {...fadeUp} className="mb-12 max-w-3xl">
        <p className={kicker}>How it works</p>
        <h2
          className={`${displaySection} mt-4 text-4xl sm:text-5xl lg:text-6xl`}
        >
          Three quiet steps between{' '}
          <span className="text-primary">invisible</span> and interviewing.
        </h2>
      </motion.div>

      <div
        className="grid gap-5 md:grid-cols-3"
        data-testid="recruiter-how-it-works-grid"
      >
        {STEPS.map((s, i) => (
          <motion.div
            key={s.n}
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.05 + i * 0.05 }}
          >
            <Card
              className="h-full rounded-3xl border border-border/70 bg-card/70 backdrop-blur"
              data-testid={s.testid}
            >
              <CardContent className="p-7">
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <s.Icon size={18} strokeWidth={2.2} aria-hidden="true" />
                  </span>
                  <span className="font-mono-edge text-[11px] tracking-[0.24em] text-muted-foreground">
                    {s.n}
                  </span>
                </div>
                <h3 className="mt-5 text-lg font-semibold tracking-[-0.01em]">
                  {s.title}
                </h3>
                <p className="mt-2 text-[13.5px] text-muted-foreground leading-relaxed">
                  {s.body}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function RecruiterVisibilityPage() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  // Sticky 50/50 A/B assignment for the hero headline. Persisted in
  // sessionStorage via ``getOrAssignABVariant`` so the same visitor
  // sees the same variant across route changes / re-renders in this
  // tab. The chosen variant automatically flows into every downstream
  // payment / signup event via ``getReferralContext().ab_variants``.
  const [headlineVariant] = useState(() =>
    getOrAssignABVariant('recruiter_headline_v1', ['long', 'short'])
  );

  useEffect(() => {
    // Fire a single GA4 experiment_view event on mount so the two
    // variants can be split by view volume in analytics — parity with
    // the CTA-side outcome we already log through paymentTracker.
    trackEvent('experiment_view', {
      experiment_id: 'recruiter_headline_v1',
      variant_id: headlineVariant,
      page: '/recruiter-visibility',
    });
  }, [headlineVariant]);

  const onPrimaryCta = useMemo(
    () => () => {
      // On mobile the pricing card sits below the fold — scroll to it.
      // On desktop the card is already in the hero, so this is a
      // gentle "here it is" nudge.
      const el = document.querySelector('[data-testid="recruiter-pricing-card"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (user) {
        navigate('/dashboard');
      } else {
        navigate('/signup');
      }
    },
    [navigate, user]
  );

  return (
    <AppShell>
      <SeoHead
        title={
          'Résumé and LinkedIn makeovers for more interviews — Blayzrr'
        }
        description={
          'AI + expert-curated résumé and LinkedIn makeovers that highlight your strengths so recruiters see your potential clearly.'
        }
        path={'/recruiter-visibility'}
      />

      <RecruiterHero onPrimaryCta={onPrimaryCta} headlineVariant={headlineVariant} />
      <HowItWorks />
      <BeforeAfterSection />
      <RecruiterTestimonials />
      <EverythingInOneSittingSection onPrimaryCta={onPrimaryCta} />
      <FAQSection />
      <FinalCTA onPrimaryCta={onPrimaryCta} />
    </AppShell>
  );
}
